import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, ChevronDown, Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

const aboutLinks = [
  { label: "About GreatLeaf", to: "/about" },
  { label: "Leadership", to: "/leadership" },
  { label: "Careers", to: "/careers" },
];
const serviceLinks = [
  { label: "All Services", to: "/services" },
  { label: "Property Management", to: "/property-management" },
  { label: "Asset Management", to: "/asset-management" },
  { label: "Hospitality Management", to: "/hospitality-management" },
  { label: "Portfolio Transition", to: "/portfolio-transition" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const loc = useLocation();
  const isDarkHero = loc.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setMenu(null); }, [loc.pathname]);

  const solid = scrolled || !isDarkHero || open;
  const textColor = solid ? "text-[#1B3B2B]" : "text-white";

  const Dropdown = ({ id, label, links }) => (
    <div className="relative" onMouseEnter={() => setMenu(id)} onMouseLeave={() => setMenu(null)}>
      <button
        data-testid={NAV.dropdown(id)}
        className={`flex items-center gap-1.5 text-sm transition-colors duration-300 ${textColor} hover:opacity-70`}
      >
        {label} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      <AnimatePresence>
        {menu === id && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
          >
            <div className="w-64 bg-white border border-[#E5E5DF] shadow-xl rounded-sm py-2">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={NAV.link(l.to.slice(1))}
                  className="block px-5 py-2.5 text-sm text-[#121A16] hover:bg-[#F7F7F5] hover:text-[#1B3B2B] transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        solid ? "bg-white/90 backdrop-blur-xl border-b border-[#E5E5DF]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid={NAV.brand} className="flex items-center gap-2.5 group">
          <Leaf className={`w-6 h-6 transition-transform duration-500 group-hover:rotate-[18deg] ${solid ? "text-[#1B3B2B]" : "text-white"}`} strokeWidth={1.6} />
          <span className={`font-display text-xl tracking-tight ${textColor}`}>GreatLeaf<span className="text-[#275C43]"> Management</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          <Dropdown id="about" label="About" links={aboutLinks} />
          <Dropdown id="services" label="Services" links={serviceLinks} />
          <Link to="/asset-classes" data-testid={NAV.link("asset-classes")} className={`text-sm transition-colors duration-300 ${textColor} hover:opacity-70`}>Asset Classes</Link>
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <Link
            to="/contact"
            data-testid={NAV.contactCta}
            className={`rounded-sm px-6 py-2.5 text-sm font-medium transition-colors duration-300 ${
              solid ? "bg-[#1B3B2B] text-white hover:bg-[#275C43]" : "bg-white text-[#1B3B2B] hover:bg-[#E8E2D9]"
            }`}
          >
            Request a Proposal
          </Link>
        </div>

        <button data-testid={NAV.mobileToggle} onClick={() => setOpen((v) => !v)} className={`lg:hidden p-2 ${textColor}`} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-white border-b border-[#E5E5DF]"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {[...aboutLinks, ...serviceLinks, { label: "Asset Classes", to: "/asset-classes" }].map((l) => (
                <Link key={l.to} to={l.to} className="py-2.5 text-[#121A16] border-b border-[#F0F0EC]">{l.label}</Link>
              ))}
              <Link to="/contact" className="mt-4 text-center rounded-sm bg-[#1B3B2B] text-white px-6 py-3">Request a Proposal</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
